/**
 * PureSustain — Public Landing Page (Warm Ocean Clarity)
 * ======================================================
 * Single-page landing surface at `/`. All routing decisions locked:
 *   - `/`               → this page
 *   - `/mvp/*`          → existing operator MVP (untouched layout)
 *   - `/verify/:id`     → consumer QR verification (untouched, kept at root)
 *
 * Implementation rules (per /app/design_guidelines.json):
 *   - Inline hex from the locked palette — no Tailwind theme extension.
 *   - lucide-react thin-line icons (strokeWidth=1.5).
 *   - Smooth-scroll on hash links via the global `scroll-behavior: smooth`.
 *   - Sticky navbar toggles from translucent (over hero) to ivory backdrop.
 *   - No backend calls. No dependencies on MVP state.
 *   - Logo used once in the navbar, never recolored.
 */
import React, { useEffect, useState, createContext, useContext } from 'react';
import axios from 'axios';
import { Link, useLocation } from 'react-router-dom';
import {
  ShieldCheck, Snowflake, QrCode,
  ArrowRight, Anchor, Eye, Share2, FileCheck2,
  Building2, Truck, Cpu, ClipboardCheck, Sparkles,
  Thermometer, Clock, AlertTriangle, Award,
  MapPin, PackageCheck, Send, Scan, BookOpen,
  Link2, Loader2, CheckCircle2,
} from 'lucide-react';
import { COPY, SUPPORTED_LANGS } from '../i18n/landingCopy';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Language context — provides the active locale's COPY tree to every
// section. Sections call `useT()` (no prop drilling).
const LangContext = createContext('en');
const useT = () => COPY[useContext(LangContext)] || COPY.en;
const useLang = () => useContext(LangContext);

// ----- Locked palette (Warm Ocean Clarity) ------------------------------
const C = {
  anchor:   '#164C4A',
  teal:     '#248C84',
  ivory:    '#FAF8F2',
  sand:     '#F3E7D3',
  seafoam:  '#DDF2E8',
  living:   '#8ACB88',
  ink:      '#223331',
  white:    '#FFFFFF',
};

// Soft wave / contour SVG used in hero + section dividers. Kept inline
// (no extra asset request) and tinted via currentColor at low opacity.
const WaveContour = ({ className = '', flip = false }) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 1440 220"
    preserveAspectRatio="none"
    className={className}
    style={flip ? { transform: 'scaleY(-1)' } : undefined}
  >
    <path
      d="M0 120 C 240 60, 480 180, 720 110 S 1200 50, 1440 130 L 1440 220 L 0 220 Z"
      fill="currentColor"
      fillOpacity="0.5"
    />
    <path
      d="M0 160 C 320 110, 560 200, 800 150 S 1200 110, 1440 170 L 1440 220 L 0 220 Z"
      fill="currentColor"
      fillOpacity="0.35"
    />
  </svg>
);

// ----- Navbar -----------------------------------------------------------
const PublicNavbar = () => {
  const t = useT();
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <header
      data-testid="public-navbar"
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        scrolled
          ? 'bg-[#FAF8F2]/85 backdrop-blur-md border-b border-[#164C4A]/10'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-3 sm:py-4 flex items-center justify-between gap-4">
        <a
          href="#top"
          data-testid="public-navbar-brand"
          className="flex items-center group"
          aria-label={t.a11y.navHome}
        >
          <img
            src="/puresustain-logo-horizontal.png"
            alt={t.a11y.logoAlt}
            className="h-14 sm:h-[72px] lg:h-[92px] w-auto"
            style={{ imageRendering: '-webkit-optimize-contrast' }}
          />
        </a>
        <nav className="hidden md:flex items-center gap-7 text-sm font-medium" style={{ color: C.ink }}>
          <a href="#what-we-prove" data-testid="public-nav-explore" className="hover:text-[#248C84] transition-colors">{t.nav.explore}</a>
          <a href="#how-it-works" data-testid="public-nav-how-it-works" className="hover:text-[#248C84] transition-colors">{t.nav.howItWorks}</a>
          <a href="#mvp-access" data-testid="public-nav-mvp-access" className="hover:text-[#248C84] transition-colors">{t.nav.mvpAccess}</a>
        </nav>
        <div className="flex items-center gap-3 sm:gap-4">
          <LangSwitcher />
          <Link
            to="/mvp/access"
            data-testid="public-navbar-enter-mvp"
            className="inline-flex items-center gap-1.5 px-4 sm:px-5 py-2 sm:py-2.5 rounded-full text-sm font-semibold transition-all shadow-sm hover:shadow-md text-white"
            style={{ background: C.teal }}
            onMouseEnter={(e) => (e.currentTarget.style.background = C.anchor)}
            onMouseLeave={(e) => (e.currentTarget.style.background = C.teal)}
          >
            {t.nav.enterMvp}
            <ArrowRight className="w-4 h-4" strokeWidth={1.75} />
          </Link>
        </div>
      </div>
    </header>
  );
};

// ----- Language switcher -----------------------------------------------
// Two small flag buttons (SVG, ~22px wide). Active locale gets a thin
// border + subtle background; the inactive locale fades slightly on
// hover. Lives only on the public landing page.
const LangSwitcher = () => {
  const lang = useLang();
  const t = useT();
  const baseClass =
    'inline-flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-md transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#248C84]/50';
  const activeClass = 'opacity-100';
  const inactiveClass = 'opacity-60 hover:opacity-100';
  return (
    <div
      role="group"
      aria-label={t.lang.groupLabel}
      data-testid="public-lang-switcher"
      className="flex items-center gap-1.5"
    >
      <Link
        to="/"
        data-testid="public-lang-en"
        aria-label={t.lang.enLabel}
        aria-current={lang === 'en' ? 'true' : 'false'}
        className={`${baseClass} ${lang === 'en' ? activeClass : inactiveClass}`}
        style={{
          border: lang === 'en' ? `1px solid ${C.teal}` : '1px solid transparent',
          background: lang === 'en' ? `${C.teal}14` : 'transparent',
        }}
      >
        <FlagUK className="w-5 h-5 sm:w-[22px] sm:h-[22px] rounded-sm" />
      </Link>
      <Link
        to="/no"
        data-testid="public-lang-no"
        aria-label={t.lang.noLabel}
        aria-current={lang === 'no' ? 'true' : 'false'}
        className={`${baseClass} ${lang === 'no' ? activeClass : inactiveClass}`}
        style={{
          border: lang === 'no' ? `1px solid ${C.teal}` : '1px solid transparent',
          background: lang === 'no' ? `${C.teal}14` : 'transparent',
        }}
      >
        <FlagNO className="w-5 h-5 sm:w-[22px] sm:h-[22px] rounded-sm" />
      </Link>
    </div>
  );
};

// Inline SVG flags — renders consistently across OS / browser
// (emoji flag glyphs do not render on Windows / many Chromes).
const FlagUK = ({ className = '' }) => (
  <svg viewBox="0 0 60 30" className={className} aria-hidden="true" focusable="false">
    <clipPath id="psuk-c"><path d="M0,0 v30 h60 v-30 z" /></clipPath>
    <clipPath id="psuk-t"><path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z" /></clipPath>
    <g clipPath="url(#psuk-c)">
      <path d="M0,0 v30 h60 v-30 z" fill="#012169" />
      <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6" />
      <path d="M0,0 L60,30 M60,0 L0,30" clipPath="url(#psuk-t)" stroke="#C8102E" strokeWidth="4" />
      <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10" />
      <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6" />
    </g>
  </svg>
);

const FlagNO = ({ className = '' }) => (
  <svg viewBox="0 0 22 16" className={className} aria-hidden="true" focusable="false">
    <rect width="22" height="16" fill="#EF2B2D" />
    <rect x="6" width="2" height="16" fill="#fff" />
    <rect y="7" width="22" height="2" fill="#fff" />
    <rect x="6.5" width="1" height="16" fill="#002868" />
    <rect y="7.5" width="22" height="1" fill="#002868" />
  </svg>
);

// ----- Hero -------------------------------------------------------------
const Hero = () => {
  const t = useT();
  // Code-side metadata for cards: icon refs + accent colour. Text comes
  // from translations keyed by card id.
  const HERO_CARDS = [
    { id: 'batch-proof', icon: PackageCheck, accent: C.teal,   itemIcons: [MapPin, ClipboardCheck, Send, QrCode] },
    { id: 'cold-chain',  icon: Snowflake,    accent: C.teal,   itemIcons: [Thermometer, Clock, AlertTriangle, Award] },
    { id: 'compliance',  icon: FileCheck2,   accent: C.anchor, itemIcons: [ClipboardCheck, Thermometer, Link2, ShieldCheck] },
    { id: 'qr-verify',   icon: QrCode,       accent: C.teal,   itemIcons: [Scan, BookOpen, ShieldCheck, Share2] },
  ];
  return (
  <section
    id="top"
    data-testid="public-hero"
    className="relative overflow-hidden pt-40 sm:pt-48 lg:pt-56 pb-24 sm:pb-32 scroll-mt-32 lg:scroll-mt-40"
    style={{
      background: `linear-gradient(180deg, ${C.ivory} 0%, ${C.seafoam} 100%)`,
    }}
  >
    <div className="absolute inset-x-0 bottom-0 text-[#248C84] pointer-events-none">
      <WaveContour className="w-full h-32 sm:h-48" />
    </div>
    <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
      <div className="lg:col-span-7">
        <p
          data-testid="public-hero-eyebrow"
          className="text-xs sm:text-sm font-semibold uppercase tracking-[0.18em] mb-5"
          style={{ color: C.teal }}
        >
          {t.hero.eyebrow}
        </p>
        <h1
          data-testid="public-hero-headline"
          className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight"
          style={{ color: C.anchor, fontFamily: 'Manrope, Satoshi, sans-serif' }}
        >
          {t.hero.headline1}
          <br />
          <span style={{ color: C.teal }}>{t.hero.headline2}</span>
        </h1>
        <p
          data-testid="public-hero-sub"
          className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed"
          style={{ color: C.ink }}
        >
          {t.hero.sub}
        </p>
        <div className="mt-8 sm:mt-10 flex flex-wrap items-center gap-3 sm:gap-4">
          <a
            href="#what-we-prove"
            data-testid="public-hero-cta-explore"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm sm:text-base font-semibold shadow-md hover:shadow-lg transition-all text-white"
            style={{ background: C.teal }}
            onMouseEnter={(e) => (e.currentTarget.style.background = C.anchor)}
            onMouseLeave={(e) => (e.currentTarget.style.background = C.teal)}
          >
            {t.hero.ctaPrimary}
            <ArrowRight className="w-4 h-4" strokeWidth={1.75} />
          </a>
          <a
            href="#mvp-access"
            data-testid="public-hero-cta-mvp"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm sm:text-base font-semibold transition-all border"
            style={{ color: C.anchor, borderColor: C.anchor }}
            onMouseEnter={(e) => { e.currentTarget.style.background = C.anchor; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = C.anchor; }}
          >
            {t.hero.ctaSecondary}
          </a>
        </div>
      </div>

      {/* Conceptual product-preview cards — no live MVP data shown. */}
      <div className="lg:col-span-5 relative" data-testid="public-hero-concept-grid">
        <div className="grid grid-cols-2 gap-4 sm:gap-5">
          {HERO_CARDS.map((card) => {
            const TitleIcon = card.icon;
            const copy = t.heroCards[card.id];
            return (
              <div
                key={card.id}
                data-testid={`public-hero-concept-${card.id}`}
                // Identical hover behaviour for all four cards: a subtle -4px lift
                // with a stronger shadow, 300ms ease-out. No per-card stagger.
                className="group relative bg-white rounded-[18px] p-4 sm:p-5 border transition-all duration-300 ease-out hover:-translate-y-1"
                style={{
                  borderColor: `${C.teal}1F`,
                  boxShadow: '0 18px 40px -24px rgba(22,76,74,0.28)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 26px 50px -22px rgba(22,76,74,0.42)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 18px 40px -24px rgba(22,76,74,0.28)';
                }}
              >
                <div className="flex items-center gap-2.5 mb-3">
                  <span
                    className="inline-flex w-9 h-9 rounded-xl items-center justify-center"
                    style={{ background: C.seafoam, color: card.accent }}
                  >
                    <TitleIcon className="w-4.5 h-4.5" strokeWidth={1.6} />
                  </span>
                  <div className="min-w-0">
                    <p
                      className="text-[13px] sm:text-sm font-bold leading-tight"
                      style={{ color: C.anchor, fontFamily: 'Manrope, sans-serif' }}
                    >
                      {copy.title}
                    </p>
                  </div>
                </div>
                <p
                  className="text-[11px] sm:text-xs leading-relaxed mb-3"
                  style={{ color: C.ink, opacity: 0.85 }}
                >
                  {copy.subtitle}
                </p>
                <ul className="space-y-1.5">
                  {copy.items.map((label, j) => {
                    const ItIcon = card.itemIcons[j];
                    return (
                      <li key={j} className="flex items-center gap-2 text-[11px] sm:text-xs" style={{ color: C.ink }}>
                        <span
                          className="inline-flex w-5 h-5 rounded-md items-center justify-center flex-shrink-0"
                          style={{ background: C.ivory, color: card.accent }}
                        >
                          <ItIcon className="w-3 h-3" strokeWidth={1.75} />
                        </span>
                        <span className="truncate">{label}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  </section>
  );
};

// ----- Trust statement --------------------------------------------------
const TrustStatement = () => {
  const t = useT();
  return (
  <section
    data-testid="public-trust-statement"
    className="relative py-20 sm:py-28"
    style={{ background: C.ivory }}
  >
    <div className="max-w-4xl mx-auto px-6 sm:px-8 text-center">
      <Sparkles className="w-7 h-7 mx-auto mb-5" strokeWidth={1.5} style={{ color: C.teal }} />
      <p
        className="text-xl sm:text-2xl lg:text-3xl font-semibold leading-snug"
        style={{ color: C.anchor, fontFamily: 'Manrope, Satoshi, sans-serif' }}
      >
        {t.trust}
      </p>
    </div>
  </section>
  );
};

// ----- Problem framing --------------------------------------------------
const ProblemFraming = () => {
  const t = useT();
  const items = t.problem.cards;
  return (
    <section
      data-testid="public-problem-section"
      className="py-20 sm:py-28"
      style={{ background: C.sand }}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="max-w-2xl mb-12 sm:mb-16">
          <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.18em] mb-3" style={{ color: C.teal }}>
            {t.problem.eyebrow}
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" style={{ color: C.anchor, fontFamily: 'Manrope, Satoshi, sans-serif' }}>
            {t.problem.title}
          </h2>
        </div>
        <div className="grid sm:grid-cols-3 gap-5 sm:gap-6">
          {items.map((it, i) => (
            <div
              key={i}
              data-testid={`public-problem-card-${i}`}
              className="rounded-[22px] p-7 transition-transform hover:-translate-y-0.5"
              style={{ background: C.white, boxShadow: '0 10px 30px -16px rgba(22,76,74,0.18)' }}
            >
              <span className="inline-flex w-10 h-10 rounded-full items-center justify-center mb-5" style={{ background: C.seafoam, color: C.teal }}>
                <Eye className="w-5 h-5" strokeWidth={1.5} />
              </span>
              <h3 className="text-lg font-semibold mb-2" style={{ color: C.anchor, fontFamily: 'Manrope, sans-serif' }}>
                {it.title}
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: C.ink }}>{it.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ----- What PureSustain proves -----------------------------------------
const WhatWeProve = () => {
  const t = useT();
  const ICONS = [ClipboardCheck, Snowflake, FileCheck2, QrCode];
  return (
    <section
      id="what-we-prove"
      data-testid="public-what-we-prove"
      className="py-24 sm:py-32 scroll-mt-32 lg:scroll-mt-40"
      style={{ background: C.ivory }}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="max-w-2xl mb-14 sm:mb-16">
          <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.18em] mb-3" style={{ color: C.teal }}>
            {t.proof.eyebrow}
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" style={{ color: C.anchor, fontFamily: 'Manrope, Satoshi, sans-serif' }}>
            {t.proof.title}
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
          {t.proof.tiles.map((tile, i) => {
            const Icon = ICONS[i];
            return (
              <div
                key={i}
                data-testid={`public-prove-tile-${i}`}
                className="rounded-[22px] p-7 transition-transform hover:-translate-y-0.5 border"
                style={{ background: C.white, borderColor: `${C.teal}1F`, boxShadow: '0 8px 26px -18px rgba(22,76,74,0.20)' }}
              >
                <span className="inline-flex w-12 h-12 rounded-2xl items-center justify-center mb-5" style={{ background: C.seafoam, color: C.teal }}>
                  <Icon className="w-6 h-6" strokeWidth={1.5} />
                </span>
                <h3 className="text-lg font-semibold mb-2" style={{ color: C.anchor, fontFamily: 'Manrope, sans-serif' }}>
                  {tile.title}
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: C.ink }}>{tile.body}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ----- How it works -----------------------------------------------------
const HowItWorks = () => {
  const t = useT();
  const ICONS = [ClipboardCheck, ShieldCheck, Share2];
  return (
    <section
      id="how-it-works"
      data-testid="public-how-it-works"
      className="relative py-24 sm:py-32 overflow-hidden scroll-mt-32 lg:scroll-mt-40"
      style={{ background: C.seafoam }}
    >
      <div className="absolute inset-x-0 top-0 text-[#FAF8F2] pointer-events-none">
        <WaveContour className="w-full h-24" flip />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="max-w-2xl mb-14 sm:mb-16">
          <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.18em] mb-3" style={{ color: C.teal }}>
            {t.how.eyebrow}
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" style={{ color: C.anchor, fontFamily: 'Manrope, Satoshi, sans-serif' }}>
            {t.how.title}
          </h2>
        </div>
        <div className="grid sm:grid-cols-3 gap-6 sm:gap-8">
          {t.how.steps.map((s, i) => {
            const Icon = ICONS[i];
            return (
              <div
                key={i}
                data-testid={`public-step-${i}`}
                className="rounded-[22px] p-7 sm:p-8 relative"
                style={{ background: C.white, boxShadow: '0 14px 36px -22px rgba(22,76,74,0.22)' }}
              >
                <span
                  className="absolute -top-4 left-7 inline-flex w-10 h-10 rounded-full items-center justify-center text-sm font-bold text-white"
                  style={{ background: C.anchor }}
                >
                  {i + 1}
                </span>
                <Icon className="w-8 h-8 mt-2 mb-4" strokeWidth={1.5} style={{ color: C.teal }} />
                <h3 className="text-xl font-semibold mb-2" style={{ color: C.anchor, fontFamily: 'Manrope, sans-serif' }}>
                  {s.title}
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: C.ink }}>{s.body}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ----- Audience strip ---------------------------------------------------
const AudienceStrip = () => {
  const t = useT();
  const ICONS = [Building2, Truck, Cpu, ClipboardCheck];
  return (
    <section
      data-testid="public-audience-strip"
      className="py-20 sm:py-28"
      style={{ background: C.ivory }}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
        <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.18em] mb-3" style={{ color: C.teal }}>
          {t.audience.eyebrow}
        </p>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight max-w-3xl mx-auto" style={{ color: C.anchor, fontFamily: 'Manrope, Satoshi, sans-serif' }}>
          {t.audience.title}
        </h2>
        <div className="mt-12 sm:mt-14 grid grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
          {t.audience.cards.map((label, i) => {
            const Icon = ICONS[i];
            return (
              <div
                key={i}
                data-testid={`public-audience-${i}`}
                className="flex flex-col items-center gap-3 rounded-[22px] py-8 px-5 transition-transform hover:-translate-y-0.5"
                style={{ background: C.white, boxShadow: '0 6px 22px -16px rgba(22,76,74,0.18)' }}
              >
                <span className="inline-flex w-12 h-12 rounded-full items-center justify-center" style={{ background: C.seafoam, color: C.teal }}>
                  <Icon className="w-6 h-6" strokeWidth={1.5} />
                </span>
                <p className="text-sm font-semibold" style={{ color: C.anchor, fontFamily: 'Manrope, sans-serif' }}>
                  {label}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ----- Pilot Access section --------------------------------------------
const PilotAccess = () => {
  const t = useT();
  return (
  <section
    id="pilot-access"
    data-testid="public-pilot-access"
    className="py-20 sm:py-24 scroll-mt-32 lg:scroll-mt-40"
    style={{ background: C.seafoam }}
  >
    <div className="max-w-3xl mx-auto px-6 sm:px-8 text-center">
      <p
        className="text-xs sm:text-sm font-semibold uppercase tracking-[0.18em] mb-3"
        style={{ color: C.teal }}
      >
        {t.pilot.eyebrow}
      </p>
      <h2
        className="text-3xl sm:text-4xl font-bold tracking-tight mb-5"
        style={{ color: C.anchor, fontFamily: 'Manrope, Satoshi, sans-serif' }}
      >
        {t.pilot.title}
      </h2>
      <p className="text-base sm:text-lg leading-relaxed" style={{ color: C.ink }}>
        {t.pilot.body}
      </p>
      <p className="mt-4 text-sm leading-relaxed" style={{ color: C.ink, opacity: 0.85 }}>
        {t.pilot.support}
      </p>
      <a
        href="#contact"
        data-testid="public-pilot-access-cta"
        className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-base font-semibold shadow-md hover:shadow-lg transition-all text-white"
        style={{ background: C.teal }}
        onMouseEnter={(e) => (e.currentTarget.style.background = C.anchor)}
        onMouseLeave={(e) => (e.currentTarget.style.background = C.teal)}
      >
        {t.pilot.cta}
        <ArrowRight className="w-4 h-4" strokeWidth={1.75} />
      </a>
    </div>
  </section>
  );
};

// ----- Team section -----------------------------------------------------
const Team = () => {
  const t = useT();
  return (
    <section
      id="team"
      data-testid="public-team"
      className="py-24 sm:py-28 scroll-mt-32 lg:scroll-mt-40"
      style={{ background: C.ivory }}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="max-w-3xl mb-12 sm:mb-14">
          <p
            className="text-xs sm:text-sm font-semibold uppercase tracking-[0.18em] mb-3"
            style={{ color: C.teal }}
          >
            {t.team.eyebrow}
          </p>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight"
            style={{ color: C.anchor, fontFamily: 'Manrope, Satoshi, sans-serif' }}
          >
            {t.team.title}
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 gap-6 sm:gap-8 max-w-5xl">
          {t.team.members.map((m) => (
            <article
              key={m.id}
              data-testid={`public-team-${m.id}`}
              className="rounded-[22px] overflow-hidden bg-white border transition-all duration-300 ease-out hover:-translate-y-1"
              style={{
                borderColor: `${C.teal}1F`,
                boxShadow: '0 14px 36px -22px rgba(22,76,74,0.22)',
              }}
            >
              <div className="aspect-square w-full overflow-hidden" style={{ background: C.sand }}>
                <img
                  src={m.photo}
                  alt={`${m.name} \u2014 ${m.role}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="p-6 sm:p-7">
                <h3
                  className="text-xl font-bold"
                  style={{ color: C.anchor, fontFamily: 'Manrope, sans-serif' }}
                >
                  {m.name}
                </h3>
                <p
                  className="text-xs uppercase tracking-[0.16em] font-semibold mt-1 mb-4"
                  style={{ color: C.teal }}
                >
                  {m.role}
                </p>
                <p className="text-sm leading-relaxed mb-4" style={{ color: C.ink }}>
                  {m.bio}
                </p>
                <p
                  className="text-xs leading-relaxed"
                  style={{ color: C.ink, opacity: 0.75 }}
                >
                  <span className="font-semibold" style={{ color: C.anchor }}>{m.focusLabel}</span>{' '}
                  {m.focus}
                </p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

// ----- Contact section --------------------------------------------------
const Contact = () => {
  const t = useT();
  const f = t.contact.form;
  const [form, setForm] = useState({
    name: '', company: '', email: '', role: '',
    interest_area: 'Pilot', message: '',
  });
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [error, setError] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    setError('');
    try {
      await axios.post(`${API_BASE}/api/pilot-requests`, {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        role: form.role.trim() || null,
        interest_area: form.interest_area,
        message: form.message.trim() || null,
      });
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setError(err?.response?.data?.detail || f.defaultError);
    }
  };

  return (
    <section
      id="contact"
      data-testid="public-contact"
      className="py-24 sm:py-32 scroll-mt-32 lg:scroll-mt-40"
      style={{ background: C.sand }}
    >
      <div className="max-w-6xl mx-auto px-6 sm:px-8 grid lg:grid-cols-5 gap-10 lg:gap-14">
        <div className="lg:col-span-2">
          <p
            className="text-xs sm:text-sm font-semibold uppercase tracking-[0.18em] mb-3"
            style={{ color: C.teal }}
          >
            {t.contact.eyebrow}
          </p>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight"
            style={{ color: C.anchor, fontFamily: 'Manrope, Satoshi, sans-serif' }}
          >
            {t.contact.title}
          </h2>
          <p className="mt-5 text-base leading-relaxed" style={{ color: C.ink }}>
            {t.contact.body}
          </p>
          <a
            href="mailto:contact@puresustainchain.eu"
            data-testid="public-contact-email"
            className="mt-6 inline-flex items-center gap-2 text-sm font-semibold transition-colors hover:opacity-80"
            style={{ color: C.teal }}
          >
            contact@puresustainchain.eu
          </a>
        </div>

        <div className="lg:col-span-3">
          {status === 'success' ? (
            <div
              data-testid="public-contact-success"
              className="rounded-[22px] p-8 sm:p-10 text-center bg-white"
              style={{ boxShadow: '0 14px 36px -22px rgba(22,76,74,0.22)' }}
            >
              <span
                className="inline-flex w-14 h-14 rounded-full items-center justify-center mb-5"
                style={{ background: C.seafoam, color: C.teal }}
              >
                <CheckCircle2 className="w-7 h-7" strokeWidth={1.6} />
              </span>
              <h3
                className="text-2xl font-bold mb-2"
                style={{ color: C.anchor, fontFamily: 'Manrope, sans-serif' }}
              >
                {f.successTitle}
              </h3>
              <p className="text-sm leading-relaxed max-w-sm mx-auto" style={{ color: C.ink }}>
                {f.successBody}
              </p>
            </div>
          ) : (
            <form
              data-testid="public-contact-form"
              onSubmit={onSubmit}
              className="rounded-[22px] p-7 sm:p-9 bg-white"
              style={{ boxShadow: '0 14px 36px -22px rgba(22,76,74,0.22)' }}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FieldLabel label={f.labelName} required>
                  <input
                    data-testid="public-contact-input-name"
                    type="text" required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full rounded-lg border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#248C84]/40 transition-shadow"
                    style={{ borderColor: `${C.anchor}22`, color: C.ink }}
                    placeholder={f.placeholderName}
                    maxLength={160}
                  />
                </FieldLabel>
                <FieldLabel label={f.labelCompany}>
                  <input
                    data-testid="public-contact-input-company"
                    type="text"
                    value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                    className="w-full rounded-lg border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#248C84]/40 transition-shadow"
                    style={{ borderColor: `${C.anchor}22`, color: C.ink }}
                    placeholder={f.placeholderCompany}
                    maxLength={200}
                  />
                </FieldLabel>
                <FieldLabel label={f.labelEmail} required>
                  <input
                    data-testid="public-contact-input-email"
                    type="email" required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full rounded-lg border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#248C84]/40 transition-shadow"
                    style={{ borderColor: `${C.anchor}22`, color: C.ink }}
                    placeholder={f.placeholderEmail}
                    maxLength={200}
                  />
                </FieldLabel>
                <FieldLabel label={f.labelRole}>
                  <input
                    data-testid="public-contact-input-role"
                    type="text"
                    value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                    className="w-full rounded-lg border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#248C84]/40 transition-shadow"
                    style={{ borderColor: `${C.anchor}22`, color: C.ink }}
                    placeholder={f.placeholderRole}
                    maxLength={120}
                  />
                </FieldLabel>
              </div>

              <FieldLabel label={f.labelInterest} className="mt-4">
                <select
                  data-testid="public-contact-input-interest"
                  value={form.interest_area}
                  onChange={(e) => setForm({ ...form, interest_area: e.target.value })}
                  className="w-full rounded-lg border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#248C84]/40 transition-shadow bg-white"
                  style={{ borderColor: `${C.anchor}22`, color: C.ink }}
                >
                  {f.interestOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </FieldLabel>

              <FieldLabel label={f.labelMessage} className="mt-4">
                <textarea
                  data-testid="public-contact-input-message"
                  rows={4}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full rounded-lg border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#248C84]/40 transition-shadow resize-none"
                  style={{ borderColor: `${C.anchor}22`, color: C.ink }}
                  placeholder={f.placeholderMessage}
                  maxLength={2000}
                />
              </FieldLabel>

              {error && (
                <p data-testid="public-contact-error" className="mt-3 text-sm" style={{ color: '#B45F1B' }}>
                  {error}
                </p>
              )}

              <div className="mt-6">
                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  data-testid="public-contact-submit"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-white transition-all shadow-sm hover:shadow-md disabled:opacity-70 disabled:cursor-not-allowed"
                  style={{ background: C.teal }}
                  onMouseEnter={(e) => { if (status !== 'submitting') e.currentTarget.style.background = C.anchor; }}
                  onMouseLeave={(e) => { if (status !== 'submitting') e.currentTarget.style.background = C.teal; }}
                >
                  {status === 'submitting' ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />
                      {f.sending}
                    </>
                  ) : (
                    <>
                      {f.submit}
                      <ArrowRight className="w-4 h-4" strokeWidth={1.75} />
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

const FieldLabel = ({ label, required, children, className = '' }) => (
  <label className={`block ${className}`}>
    <span
      className="block text-[11px] uppercase tracking-[0.14em] font-semibold mb-1.5"
      style={{ color: C.anchor }}
    >
      {label} {required && <span style={{ color: '#B45F1B' }}>*</span>}
    </span>
    {children}
  </label>
);

// ----- MVP Access section ----------------------------------------------
const MvpAccess = () => {
  const t = useT();
  return (
  <section
    id="mvp-access"
    data-testid="public-mvp-access"
    className="py-24 sm:py-32 scroll-mt-32 lg:scroll-mt-40"
    style={{ background: C.sand }}
  >
    <div className="max-w-4xl mx-auto px-6 sm:px-8 text-center">
      <Anchor className="w-8 h-8 mx-auto mb-5" strokeWidth={1.5} style={{ color: C.teal }} />
      <h2
        data-testid="public-mvp-access-heading"
        className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-5"
        style={{ color: C.anchor, fontFamily: 'Manrope, Satoshi, sans-serif' }}
      >
        {t.mvp.heading}
      </h2>
      <p
        data-testid="public-mvp-access-body"
        className="text-base sm:text-lg leading-relaxed max-w-2xl mx-auto"
        style={{ color: C.ink }}
      >
        {t.mvp.body}
      </p>
      <div className="mt-9 flex flex-wrap items-center justify-center gap-3 sm:gap-4">
        <Link
          to="/mvp/access"
          data-testid="public-mvp-access-enter"
          className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-base font-semibold shadow-md hover:shadow-lg transition-all text-white"
          style={{ background: C.teal }}
          onMouseEnter={(e) => (e.currentTarget.style.background = C.anchor)}
          onMouseLeave={(e) => (e.currentTarget.style.background = C.teal)}
        >
          {t.mvp.enter}
          <ArrowRight className="w-4 h-4" strokeWidth={1.75} />
        </Link>
        <a
          href="#pilot-access"
          data-testid="public-mvp-access-request-pilot"
          className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-base font-semibold transition-all border bg-transparent"
          style={{ color: C.anchor, borderColor: C.anchor }}
          onMouseEnter={(e) => { e.currentTarget.style.background = C.anchor; e.currentTarget.style.color = '#fff'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = C.anchor; }}
        >
          {t.mvp.request}
        </a>
      </div>
    </div>
  </section>
  );
};

// ----- Footer -----------------------------------------------------------
const Footer = () => {
  const t = useT();
  return (
  <footer
    data-testid="public-footer"
    className="relative pt-16 pb-10 text-[#DDF2E8]"
    style={{ background: C.anchor }}
  >
    <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid sm:grid-cols-3 gap-10">
      <div>
        <p
          data-testid="public-footer-brand"
          className="text-xl font-semibold tracking-tight text-white mb-3"
          style={{ fontFamily: 'Manrope, Satoshi, sans-serif' }}
        >
          PureSustain AS
        </p>
        <p
          className="text-xs uppercase tracking-[0.22em] font-semibold mb-4"
          style={{ color: '#8ACB88' }}
        >
          {t.footer.tagline}
        </p>
        <p className="text-sm leading-relaxed text-[#DDF2E8]/80 max-w-sm">
          {t.footer.description}
        </p>
      </div>
      <div>
        <p className="text-xs uppercase tracking-[0.18em] font-semibold text-white/80 mb-4">{t.footer.contactLabel}</p>
        <a
          href="mailto:contact@puresustainchain.eu"
          data-testid="public-footer-email"
          className="block text-sm text-[#DDF2E8] hover:text-white transition-colors"
        >
          contact@puresustainchain.eu
        </a>
        <a
          href="#pilot-access"
          data-testid="public-footer-request-pilot"
          className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#8ACB88] hover:text-white transition-colors"
        >
          {t.footer.requestPilot}
          <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.75} />
        </a>
      </div>
      <div>
        <p className="text-xs uppercase tracking-[0.18em] font-semibold text-white/80 mb-4">{t.footer.platformLabel}</p>
        <ul className="space-y-2 text-sm">
          <li><a href="#what-we-prove" className="text-[#DDF2E8] hover:text-white transition-colors">{t.footer.linkExplore}</a></li>
          <li><a href="#how-it-works" className="text-[#DDF2E8] hover:text-white transition-colors">{t.footer.linkHow}</a></li>
          <li><a href="#mvp-access" className="text-[#DDF2E8] hover:text-white transition-colors">{t.footer.linkMvp}</a></li>
          <li>
            <Link to="/mvp/access" data-testid="public-footer-enter-mvp" className="text-[#8ACB88] hover:text-white font-semibold transition-colors">
              {t.footer.linkEnter}
            </Link>
          </li>
        </ul>
      </div>
    </div>
    <div
      data-testid="public-footer-copyright"
      className="mt-12 pt-6 border-t border-white/10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-xs text-[#DDF2E8]/60"
    >
      {t.footer.copyright}
    </div>
  </footer>
  );
};

// ----- Page composition -------------------------------------------------
// Accepts `lang` prop ('en' or 'no'). Defaults to 'en' if a route forgets
// to pass it. Wraps every section in a LangContext provider so each
// component can simply call `useT()`.
const LandingPage = ({ lang: langProp }) => {
  // Treat the URL path as the single source of truth for the active
  // locale. The `/no` route mounts this component with lang="no"; the
  // `/` route passes "en". The path-based fallback keeps the switcher's
  // active state in sync even during hot reloads / direct deep links.
  const location = useLocation();
  const lang = SUPPORTED_LANGS.includes(langProp)
    ? langProp
    : (location.pathname.startsWith('/no') ? 'no' : 'en');

  // Ensure smooth scroll for hash anchors regardless of any global override.
  useEffect(() => {
    const prev = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = 'smooth';
    return () => { document.documentElement.style.scrollBehavior = prev; };
  }, []);

  // Keep <html lang> in sync for accessibility / search engines.
  useEffect(() => {
    const prev = document.documentElement.lang;
    document.documentElement.lang = lang;
    return () => { document.documentElement.lang = prev; };
  }, [lang]);

  return (
    <LangContext.Provider value={lang}>
      <div data-testid="public-landing-root" data-lang={lang} style={{ background: C.ivory, color: C.ink, fontFamily: 'Inter, system-ui, sans-serif' }}>
        <PublicNavbar />
        <main>
          <Hero />
          <TrustStatement />
          <ProblemFraming />
          <WhatWeProve />
          <HowItWorks />
          <AudienceStrip />
          <MvpAccess />
          <PilotAccess />
          <Team />
          <Contact />
        </main>
        <Footer />
      </div>
    </LangContext.Provider>
  );
};

export default LandingPage;
